'use client';

import { c as _c } from "react/compiler-runtime";
import { useAuth, useUpdateProfile } from '@morfi/backend-sdk-v2/client';
import { useEffect, useRef } from 'react';
import { clearOnboardingPersistPending, isOnboardingCompleted, isOnboardingPersistPending } from '@/app/[locale]/onboarding/_hooks/use-onboarding-store';
import { logger } from '@/lib/telemetry/logger';
const ONBOARDING_COMPLETION_MUTATION_OPTIONS = {
  meta: {
    skipGlobalErrorHandler: true
  }
} as const;

/**
 * App-wide retry loop for the onboarding-completion backend write (MOR-1784).
 *
 * Completion persistence used to be a single fire-and-forget PATCH — one
 * transient failure (e.g. a 401 while the session rotates) lost it forever,
 * and because logout wipes the localStorage completion record, the next login
 * re-onboarded the user. This component heals that: whenever an authenticated
 * user's device says onboarding is done (the durable pending flag, or the
 * local completion record) but the auth user does not, it re-sends the PATCH
 * until the backend acknowledges it.
 *
 * Mounted in `GlobalAuthHandlers` so it runs on every route. Renders nothing.
 */
export function OnboardingCompletionSync() {
  const $ = _c(6);
  const {
    isAuthenticated,
    user,
    setUser
  } = useAuth();
  const {
    mutateAsync: updateProfile
  } = useUpdateProfile(ONBOARDING_COMPLETION_MUTATION_OPTIONS);
  const inFlightRef = useRef(false);
  let t0;
  let t1;
  if ($[0] !== isAuthenticated || $[1] !== setUser || $[2] !== updateProfile || $[3] !== user) {
    t0 = () => {
      if (!isAuthenticated || user === null) {
        return;
      }
      if (user.onboardingCompleted === true) {
        clearOnboardingPersistPending();
        return;
      }
      if (!(isOnboardingPersistPending() || isOnboardingCompleted())) {
        return;
      }
      if (inFlightRef.current) {
        return;
      }
      inFlightRef.current = true;
      updateProfile({
        onboardingCompleted: true
      }).then(() => {
        clearOnboardingPersistPending();
        setUser({
          ...user,
          onboardingCompleted: true
        });
      }).catch(_temp).finally(() => {
        inFlightRef.current = false;
      });
    };
    t1 = [isAuthenticated, user, setUser, updateProfile];
    $[0] = isAuthenticated;
    $[1] = setUser;
    $[2] = updateProfile;
    $[3] = user;
    $[4] = t0;
    $[5] = t1;
  } else {
    t0 = $[4];
    t1 = $[5];
  }
  useEffect(t0, t1);
  return null;
}
function _temp(err) {
  logger.error("onboarding-completion-sync: backend write failed, will retry", {
    error: err instanceof Error ? err.message.slice(0, 200) : String(err)
  });
}